import { useEffect, useId, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';

import { IProfile } from 'models/profile/i-profile.interface';
import { IState } from 'models/reducers/i-state.interface';

import { ROUTES } from 'constants/routes.constant';

import { useMenuVisibility } from 'contexts/MenuVisibilityContext';

import { centralEllipsis } from 'utils/ellipsis.utils';

import ProfileAvatarDefault from 'assets/profile/icons/profile-avatar-default.svg';
import ProfileAvatarReferral from 'assets/profile/icons/profile-avatar-referral.svg';

import Icon from '../../icon/Icon';
import MenuAccountDropdown from '../menuAccountDropdown/MenuAccountDropdown';
import styles from './MenuAccountControl.module.scss';

type MenuAccountControlProps = {
  variant?: 'default' | 'referral';
};

const MenuAccountControl = ({ variant = 'default' }: MenuAccountControlProps) => {
  const id = useId();
  const {
    accountDropdownVisible: dropdownVisible,
    setAccountDropdownVisible,
    toggleAccountDropdown,
  } = useMenuVisibility();
  const [showNewNfts, setShowNewNfts] = useState(false);
  const profileData = useSelector(({ profile }: IState): IProfile | null => profile.profile);
  const { newNfts, address: walletAddress } = useSelector((state: IState) => state.wallet);
  const location = useLocation();
  const classNames = [
    showNewNfts ? styles['account-control--new-nfts'] : '',
    styles['account-control'],
    variant === 'referral' ? styles['account-control--referral'] : '',
  ];

  useEffect(() => {
    setAccountDropdownVisible(false);
  }, [location, setAccountDropdownVisible]);

  useEffect(() => {
    setShowNewNfts(true);

    const timer = setTimeout(() => {
      setShowNewNfts(false);
    }, 2000);

    return () => {
      clearTimeout(timer);
    };
  }, [newNfts]);

  useEffect(() => {
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setAccountDropdownVisible(false);
      }
    };

    document.addEventListener('keydown', onEscape, false);

    return () => {
      document.removeEventListener('keydown', onEscape, false);
    };
  }, [setAccountDropdownVisible]);

  const ProfileDefaultAvatar = variant === 'referral' ? ProfileAvatarReferral : ProfileAvatarDefault;

  return profileData && walletAddress ? (
    <div className={classNames.join(' ')}>
      {showNewNfts && newNfts ? (
        <div className={styles['account-control__new-nfts']}>
          <span>+{newNfts}</span>
        </div>
      ) : (
        <></>
      )}
      <Link to={`${ROUTES.PROFILE}/collected`} className={styles['profile-link']}>
        <div className={styles['avatar']}>
          <img alt="profile" src={profileData.avatar || ProfileDefaultAvatar} />
        </div>
        <div className={styles['user-info']}>
          <p className={styles['user-name']}>{profileData.first_name || 'Unknown'}</p>
          <p className={styles['user-address']} title={walletAddress}>
            {centralEllipsis(walletAddress, 9)}
          </p>
        </div>
      </Link>
      <button
        aria-haspopup="true"
        data-toggle="dropdown"
        aria-label="Profile menu"
        id={`account-control-dropdown-${id}`}
        className={`button--styleless ${styles['collapse-button']}`}
        aria-expanded={dropdownVisible}
        onClick={toggleAccountDropdown}
      >
        <Icon
          id="collapse-icon"
          className={`${styles['collapse-icon']} ${dropdownVisible ? styles['collapse-icon--visible'] : ''}`}
        />
      </button>
      {dropdownVisible && (
        <div className={styles['account-control__backdrop']} onClick={() => setAccountDropdownVisible(false)}></div>
      )}
      <div
        className={`${styles['account-control__dropdown']} ${
          dropdownVisible ? styles['account-control__dropdown--visible'] : ''
        }`}
      >
        <MenuAccountDropdown
          aria-labelledby={`account-control-dropdown-${id}`}
          show={dropdownVisible}
          variant={variant}
        />
      </div>
    </div>
  ) : (
    <></>
  );
};

export default MenuAccountControl;
